
import numeral from 'numeral';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  computed,
  onMounted,
} from 'vue';

import { PRESELECTED_PROJECTS_IDS, TRAFFIC_TYPES } from '@/libs/consts';

import type {
  Option,
  Projects,
  Statistics,
} from '@/types';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    Feed: defineAsyncComponent(() => import('@/components/Feed.vue')),
    Chartist: defineAsyncComponent(() => import('@/components/Chartist.vue')),
  },
  setup() {
    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entriesLoading: statisticsLoading,
      entries: statistics,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/staff/getStatisticsByFeedsActivity');

    const statisticsFilters = useUrlState('filters', {
      hour_offset: 24,
      show_feeds_requests: false,
      traffic_type: [],
      project_id: user.isSuperAdmin() ? PRESELECTED_PROJECTS_IDS : [],
      feed_id: [],
    });

    const feedsParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const showStatisticsFilters = ref<boolean>(false);

    const fetchStatisticsWrapper = async (): Promise<void> => fetchStatistics(statisticsFilters);

    const onProjectChange = (): void => {
      statisticsFilters.feed_id = [];
    };

    const hourOffsetOptions: Option[] = [3, 6, 12, 24, 48].map((value: number) => {
      const labelMap: { [key: number]: string; } = {
        48: '2 days',
      };

      return {
        value,
        label: `${labelMap[value] || value}`,
      };
    });

    const axisYLabelInterpolationFunction = (value: number): string => numeral(value).format('0a');

    const axisYLabelInterpolationFunctionRevenue = (value: number): string => numeral(value).format('$0.0[0000]');

    onMounted((): void => {
      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
        fetchStatisticsWrapper(),
      ]);
    });

    return {
      user,
      projectsLoading,
      projects,
      statisticsLoading,
      statistics,
      statisticsFilters,
      feedsParams,
      showStatisticsFilters,
      fetchStatisticsWrapper,
      onProjectChange,
      hourOffsetOptions,
      axisYLabelInterpolationFunction,
      axisYLabelInterpolationFunctionRevenue,
      TRAFFIC_TYPES,
    };
  },
});
